import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  UtensilsCrossed,
  Plus,
  Edit2,
  Trash2,
  Loader2,
  AlertTriangle,
  RefreshCw,
  Euro,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import apiClient from "@/lib/api-client";
import { useGlobalToast } from "../contexts/ToastContext";
import { useConfirm } from "../contexts/ConfirmContext";
import { usePermissions } from "../hooks/usePermissions";
import { useLocation } from "../contexts/LocationContext";
import { extractApiErrorMessage } from "../lib/api-errors";

type MenuScope = "per_guest" | "all_guests";

interface Menu {
  id: string;
  name: string;
  description: string | null;
  price: number | null;
  scope: MenuScope;
  validFrom: string;
  validTo: string;
  isActive: boolean;
  locationId: string | null;
  minPartySize: number;
}

interface FormData {
  name: string;
  description: string;
  price: string;
  scope: MenuScope;
  validFrom: string;
  validTo: string;
  isActive: boolean;
  locationId: string; // "" = todas las sedes (locationId null)
  minPartySize: number; // 1 = sin mínimo
}

const EMPTY_FORM: FormData = {
  name: "",
  description: "",
  price: "",
  scope: "per_guest",
  validFrom: "",
  validTo: "",
  isActive: true,
  locationId: "",
  minPartySize: 1,
};

const SCOPE_LABELS: Record<MenuScope, string> = {
  per_guest: "Por comensal",
  all_guests: "Toda la mesa",
};

// Valida un importe monetario en texto libre (admite coma o punto decimal,
// hasta 2 decimales). Devuelve `null` si no es válido; el llamador decide si
// vacío es aceptable (aquí el precio es opcional).
function parseMoneyAmount(value: string): number | null {
  const trimmed = value.trim().replace(",", ".");
  if (trimmed === "" || !/^\d+(\.\d{1,2})?$/.test(trimmed)) return null;
  const n = parseFloat(trimmed);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

// "24 ago – 30 ago" (sin año, salvo que Desde/Hasta caigan en años distintos).
function formatValidity(validFrom: string, validTo: string): string {
  try {
    const from = parseISO(validFrom);
    const to = parseISO(validTo);
    const sameYear = from.getFullYear() === to.getFullYear();
    const fromLabel = format(from, sameYear ? "d MMM" : "d MMM yyyy", {
      locale: es,
    });
    const toLabel = format(to, "d MMM yyyy", { locale: es });
    return `${fromLabel} – ${sameYear ? format(to, "d MMM", { locale: es }) : toLabel}`;
  } catch {
    return `${validFrom} – ${validTo}`;
  }
}

export default function Menus() {
  const { showToast } = useGlobalToast();
  const { confirm } = useConfirm();
  const { can } = usePermissions();
  const canManage = can("mesas:editar");
  const { locations, currentBookingMode } = useLocation();
  const hasMultipleLocations = locations.length > 1;

  const [menus, setMenus] = useState<Menu[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(true);

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>(EMPTY_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  useEffect(() => {
    loadMenus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showInactive]);

  const loadMenus = async () => {
    try {
      setIsLoading(true);
      setLoadError(null);
      const res = await apiClient.get("/menus", {
        params: { includeInactive: showInactive ? "true" : "false" },
      });
      setMenus(res.data?.menus ?? []);
    } catch (err) {
      setLoadError(
        extractApiErrorMessage(err, "No se pudieron cargar los menús"),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const locationLabel = (locationId: string | null): string => {
    if (!locationId) return "Todas las sedes";
    return locations.find((l) => l.id === locationId)?.name ?? "Sede eliminada";
  };

  const openCreate = () => {
    setEditingId(null);
    setFormData(EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = (m: Menu) => {
    setEditingId(m.id);
    setFormData({
      name: m.name,
      description: m.description ?? "",
      price: m.price !== null && m.price !== undefined ? String(m.price) : "",
      scope: m.scope,
      validFrom: m.validFrom.slice(0, 10),
      validTo: m.validTo.slice(0, 10),
      isActive: m.isActive,
      locationId: m.locationId ?? "",
      minPartySize: m.minPartySize ?? 1,
    });
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      showToast("El nombre es obligatorio", "error");
      return;
    }
    if (!formData.validFrom || !formData.validTo) {
      showToast("Indica las fechas Desde y Hasta de vigencia", "error");
      return;
    }
    if (formData.validFrom > formData.validTo) {
      showToast(
        "La fecha 'Hasta' no puede ser anterior a la fecha 'Desde'",
        "error",
      );
      return;
    }

    let price: number | null = null;
    if (formData.price.trim() !== "") {
      price = parseMoneyAmount(formData.price);
      if (price === null) {
        showToast(
          "El precio debe ser un número válido igual o mayor que 0",
          "error",
        );
        return;
      }
    }

    if (!Number.isInteger(formData.minPartySize) || formData.minPartySize < 1) {
      showToast(
        "El mínimo de comensales debe ser un número entero igual o mayor que 1",
        "error",
      );
      return;
    }

    const payload = {
      name: formData.name.trim(),
      description: formData.description.trim() || null,
      price,
      scope: formData.scope,
      validFrom: formData.validFrom,
      validTo: formData.validTo,
      isActive: formData.isActive,
      locationId: formData.locationId || null,
      minPartySize: formData.minPartySize,
    };

    try {
      setIsSubmitting(true);
      if (editingId) {
        await apiClient.put(`/menus/${editingId}`, payload);
        showToast("Menú actualizado");
      } else {
        await apiClient.post("/menus", payload);
        showToast("Menú creado");
      }
      setShowModal(false);
      await loadMenus();
    } catch (err) {
      showToast(extractApiErrorMessage(err, "No se pudo guardar el menú"), "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleActive = async (m: Menu) => {
    setTogglingId(m.id);
    try {
      await apiClient.put(`/menus/${m.id}`, { isActive: !m.isActive });
      await loadMenus();
    } catch (err) {
      showToast(
        extractApiErrorMessage(err, "No se pudo cambiar el estado del menú"),
        "error",
      );
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (m: Menu) => {
    if (
      !(await confirm({
        title: `¿Eliminar el menú "${m.name}"?`,
        message: "Esta acción no se puede deshacer.",
        confirmText: "Sí, eliminar",
        danger: true,
      }))
    )
      return;
    try {
      setDeletingId(m.id);
      await apiClient.delete(`/menus/${m.id}`);
      showToast("Menú eliminado");
      await loadMenus();
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      if (status === 409) {
        showToast(
          extractApiErrorMessage(
            err,
            "Este menú tiene reservas asociadas y no se puede eliminar. Desactívalo en su lugar.",
          ),
          "info",
        );
      } else {
        showToast(
          extractApiErrorMessage(err, "No se pudo eliminar el menú"),
          "error",
        );
      }
    } finally {
      setDeletingId(null);
    }
  };

  // Los menús con fechas son un concepto de mesas; en modo citas esta
  // sección no aplica. El sidebar ya oculta el enlace; esto cubre el acceso
  // por URL directa (mismo criterio que TableMap.tsx / TableCombinations.tsx).
  if (currentBookingMode === "appointments") {
    return <Navigate to="/" replace />;
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="flex flex-col items-center gap-4 text-center max-w-sm">
          <AlertTriangle className="w-10 h-10 text-red-500" />
          <p className="text-red-600">{loadError}</p>
          <button
            onClick={loadMenus}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium"
          >
            <RefreshCw className="w-4 h-4" />
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-3 md:p-6">
      {/* Header */}
      <div className="mb-4 md:mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 flex items-center gap-2">
            <UtensilsCrossed className="w-6 h-6 text-primary-600" />
            Menús
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Menús con vigencia por fechas para tu carta (menú del día,
            degustación, especial de fin de semana…)
          </p>
        </div>
        <button
          onClick={openCreate}
          disabled={!canManage}
          title={canManage ? undefined : "No tienes permiso para editar menús"}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="w-4 h-4" />
          Nuevo menú
        </button>
      </div>

      {/* Toolbar */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3 md:p-4 mb-4 flex items-center">
        <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          Mostrar inactivos
        </label>
      </div>

      {/* List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="p-12 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
          </div>
        ) : menus.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <UtensilsCrossed className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p className="font-medium text-gray-700">Aún no tienes menús</p>
            <p className="text-sm mt-1">
              Crea el primero para ofrecer un menú del día o especial con
              fechas concretas.
            </p>
          </div>
        ) : (
          <>
            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-gray-100">
              {menus.map((m) => (
                <div key={m.id} className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-gray-900 truncate">
                          {m.name}
                        </p>
                        {!m.isActive && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-200 text-gray-600 uppercase">
                            Inactivo
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {formatValidity(m.validFrom, m.validTo)}
                      </p>
                      <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                        <span
                          className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${
                            m.scope === "per_guest"
                              ? "bg-blue-100 text-blue-800"
                              : "bg-purple-100 text-purple-800"
                          }`}
                        >
                          {SCOPE_LABELS[m.scope]}
                        </span>
                        {m.price !== null && (
                          <span className="inline-flex items-center gap-0.5 text-xs text-gray-600">
                            <Euro className="w-3 h-3" />
                            {m.price.toFixed(2)}
                          </span>
                        )}
                        {hasMultipleLocations && (
                          <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                            {locationLabel(m.locationId)}
                          </span>
                        )}
                        {m.minPartySize > 1 && (
                          <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                            Mín. {m.minPartySize}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <button
                        onClick={() => toggleActive(m)}
                        disabled={!canManage || togglingId === m.id}
                        title={
                          canManage
                            ? m.isActive
                              ? "Desactivar"
                              : "Activar"
                            : "No tienes permiso para editar menús"
                        }
                        className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                          m.isActive ? "bg-primary-600" : "bg-gray-300"
                        }`}
                      >
                        {togglingId === m.id ? (
                          <Loader2 className="absolute inset-0 m-auto w-3 h-3 animate-spin text-white" />
                        ) : (
                          <span
                            className="inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform"
                            style={{
                              transform: m.isActive
                                ? "translateX(18px)"
                                : "translateX(2px)",
                            }}
                          />
                        )}
                      </button>
                      <div className="flex items-center gap-0.5 mt-1">
                        <button
                          onClick={() => openEdit(m)}
                          disabled={!canManage}
                          className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(m)}
                          disabled={!canManage}
                          className="p-1.5 hover:bg-red-50 rounded-lg text-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {deletingId === m.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 uppercase text-xs">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium">Menú</th>
                    <th className="text-left px-4 py-3 font-medium">
                      Vigencia
                    </th>
                    <th className="text-left px-4 py-3 font-medium">Precio</th>
                    <th className="text-left px-4 py-3 font-medium">
                      Política
                    </th>
                    {hasMultipleLocations && (
                      <th className="text-left px-4 py-3 font-medium">
                        Sede
                      </th>
                    )}
                    <th className="text-left px-4 py-3 font-medium">Activo</th>
                    <th className="text-right px-4 py-3 font-medium">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {menus.map((m) => (
                    <tr key={m.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium text-gray-900">{m.name}</p>
                          {m.minPartySize > 1 && (
                            <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 whitespace-nowrap">
                              Mín. {m.minPartySize}
                            </span>
                          )}
                        </div>
                        {m.description && (
                          <p className="text-xs text-gray-500 truncate max-w-xs">
                            {m.description}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                        {formatValidity(m.validFrom, m.validTo)}
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {m.price !== null ? `${m.price.toFixed(2)} €` : "—"}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                            m.scope === "per_guest"
                              ? "bg-blue-100 text-blue-800"
                              : "bg-purple-100 text-purple-800"
                          }`}
                        >
                          {SCOPE_LABELS[m.scope]}
                        </span>
                      </td>
                      {hasMultipleLocations && (
                        <td className="px-4 py-3 text-gray-600 text-xs">
                          {locationLabel(m.locationId)}
                        </td>
                      )}
                      <td className="px-4 py-3">
                        <button
                          onClick={() => toggleActive(m)}
                          disabled={!canManage || togglingId === m.id}
                          title={
                            canManage
                              ? m.isActive
                                ? "Desactivar"
                                : "Activar"
                              : "No tienes permiso para editar menús"
                          }
                          className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                            m.isActive ? "bg-primary-600" : "bg-gray-300"
                          }`}
                        >
                          {togglingId === m.id ? (
                            <Loader2 className="absolute inset-0 m-auto w-3.5 h-3.5 animate-spin text-white" />
                          ) : (
                            <span
                              className="inline-block h-4 w-4 rounded-full bg-white transition-transform"
                              style={{
                                transform: m.isActive
                                  ? "translateX(22px)"
                                  : "translateX(4px)",
                              }}
                            />
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end items-center gap-1">
                          <button
                            onClick={() => openEdit(m)}
                            disabled={!canManage}
                            className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            title={
                              canManage
                                ? "Editar"
                                : "No tienes permiso para editar menús"
                            }
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(m)}
                            disabled={!canManage}
                            className="p-2 hover:bg-red-50 rounded-lg text-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                            title={
                              canManage
                                ? "Eliminar"
                                : "No tienes permiso para editar menús"
                            }
                          >
                            {deletingId === m.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center p-0 md:p-4">
          <div className="bg-white w-full md:max-w-lg md:rounded-xl rounded-t-2xl shadow-xl max-h-[90dvh] overflow-y-auto">
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingId ? "Editar menú" : "Nuevo menú"}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
              >
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="Menú del día, Menú degustación…"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descripción
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  rows={3}
                  placeholder="Escribe aquí los platos…"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Desde <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.validFrom}
                    onChange={(e) =>
                      setFormData({ ...formData, validFrom: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Hasta <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    required
                    min={formData.validFrom || undefined}
                    value={formData.validTo}
                    onChange={(e) =>
                      setFormData({ ...formData, validTo: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Precio (€)
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={formData.price}
                  onChange={(e) =>
                    setFormData({ ...formData, price: e.target.value })
                  }
                  onFocus={(e) => e.target.select()}
                  placeholder="Opcional"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <p className="text-[11px] text-gray-500 mt-1">
                  Déjalo vacío si el precio varía o ya se indica en la carta.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Política de precio
                </label>
                <div className="space-y-2">
                  <label
                    className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                      formData.scope === "per_guest"
                        ? "border-primary-500 bg-primary-50"
                        : "border-gray-200 hover:bg-gray-50"
                    }`}
                  >
                    <input
                      type="radio"
                      name="scope"
                      checked={formData.scope === "per_guest"}
                      onChange={() =>
                        setFormData({ ...formData, scope: "per_guest" })
                      }
                      className="mt-0.5 text-primary-600 focus:ring-primary-500"
                    />
                    <span>
                      <span className="block text-sm font-medium text-gray-900">
                        Por comensal
                      </span>
                      <span className="block text-xs text-gray-500">
                        El precio se multiplica por el número de personas de
                        la reserva.
                      </span>
                    </span>
                  </label>
                  <label
                    className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                      formData.scope === "all_guests"
                        ? "border-primary-500 bg-primary-50"
                        : "border-gray-200 hover:bg-gray-50"
                    }`}
                  >
                    <input
                      type="radio"
                      name="scope"
                      checked={formData.scope === "all_guests"}
                      onChange={() =>
                        setFormData({ ...formData, scope: "all_guests" })
                      }
                      className="mt-0.5 text-primary-600 focus:ring-primary-500"
                    />
                    <span>
                      <span className="block text-sm font-medium text-gray-900">
                        Toda la mesa
                      </span>
                      <span className="block text-xs text-gray-500">
                        El precio es fijo para toda la mesa, sin importar
                        cuántos comensales sean.
                      </span>
                    </span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mínimo de comensales
                </label>
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={formData.minPartySize}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      minPartySize: parseInt(e.target.value, 10) || 1,
                    })
                  }
                  onFocus={(e) => e.target.select()}
                  className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <p className="text-[11px] text-gray-500 mt-1">
                  Las reservas con menos comensales no verán este menú. 1 =
                  sin mínimo.
                </p>
              </div>

              {hasMultipleLocations && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sede
                  </label>
                  <select
                    value={formData.locationId}
                    onChange={(e) =>
                      setFormData({ ...formData, locationId: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">Todas las sedes</option>
                    {locations.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) =>
                    setFormData({ ...formData, isActive: e.target.checked })
                  }
                  className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                Menú activo
              </label>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || !canManage}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                >
                  {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  {editingId ? "Guardar cambios" : "Crear menú"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
